import type {DesignMeta, GraphicMeta, GraphicsMetaMapType, PageHashedId, PosterHashedId} from '@Components/social-media/publish-params.types';
import type {DesignSelection, SocialPlatform} from '@Components/social-media/social-media.types';
import type {SocialMediaAccountId} from '@Components/social-media/post.vo';
import type {CreateDesignFromMediaModalOpts} from '@Components/design-selector/design-selector.types';
import type {WizardUploadDesignsCallbacks, WizardUploadMediaGa4Callbacks} from '@Libraries/social-media-wizard-design-library.types';
import {MAX_UPLOADS_TO_DESIGNS} from '@Libraries/social-media-wizard-design-library.types';
import {getPostersPageIds} from '@Libraries/poster-library';
import {GA4EventName} from '@Libraries/ga-events';
import {isUserPremium} from '@Libraries/user.library';
import {noop} from '@Utils/general.util';

const DESIGN_GALLERY_BOTTOM_OFFSET = 230;
const DESIGN_GALLERY_BOTTOM_OFFSET_MOBILE = 168;
const PUBLISH_PARAMS_STICKY_HEADER_HEIGHT = 100;
const MAX_DESIGNS_PER_POST_FOR_NON_PREMIUM_USER = 1;

export const getDesignsPerPostPlanLimit = (): number | null => {
  return isUserPremium() ? null : MAX_DESIGNS_PER_POST_FOR_NON_PREMIUM_USER;
};

export const doesPlanCapDesignsPerPost = (): boolean => {
  return getDesignsPerPostPlanLimit() !== null;
};

export const isDesignCountWithinPlanLimit = (designCount: number): boolean => {
  const planLimit = getDesignsPerPostPlanLimit();

  return planLimit === null || designCount <= planLimit;
};

export const getAddableDesignCountUnderPlanLimit = (currentDesignCount: number, maxDesignsToAdd: number): number => {
  const planLimit = getDesignsPerPostPlanLimit();

  if (planLimit === null) {
    return maxDesignsToAdd;
  }

  return Math.max(Math.min(maxDesignsToAdd, planLimit - currentDesignCount), 0);
};

export const WIZARD_UPLOAD_MEDIA_GA4_CALLBACKS: WizardUploadMediaGa4Callbacks = {
  onTriggered: (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_UPLOADMEDIA_CLICKED);
  },
  onPosterCreated: (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_UPLOADMEDIA_CREATED);
  },
};

export const getWizardUploadDesignsModalOpts = ({
  onDesignsCreated,
  onPosterCreationsStarted,
  onTriggered,
  onPosterCreated = noop,
  maxSelectionNumber = MAX_UPLOADS_TO_DESIGNS,
}: WizardUploadDesignsCallbacks): CreateDesignFromMediaModalOpts => {
  return {
    allowMultipagePosterCreation: false,
    maxSelectionNumber,
    shouldUseFastPendingImageUploadFlow: false,
    onAllPostersCreated: (posterHashedIds: PosterHashedId[]): void => {
      getDesignSelectionsForPosters(posterHashedIds)
        .then(onDesignsCreated)
        .catch((err: unknown) => {
          console.error('Error fetching poster page IDs:', err);
        });
    },
    onPosterCreated,
    onTriggered,
    onPosterCreationsStarted,
  };
};

const getDesignSelectionsForPosters = async (posterHashedIds: PosterHashedId[]): Promise<DesignSelection[]> => {
  const pageIdsMap = await getPostersPageIds(posterHashedIds);

  return Object.keys(pageIdsMap).flatMap((posterId): DesignSelection[] => {
    return pageIdsMap[posterId].map((pageId): DesignSelection => {
      return {posterHashedId: posterId, pageHashedId: pageId};
    });
  });
};

export const getGraphicMeta = async ({posterHashedId, pageHashedId}: DesignSelection): Promise<GraphicMeta> => {
  const data = (await window.PMW.readLocal('socialmedia/getDesignMeta', {
    posterId: posterHashedId,
    pageId: pageHashedId,
  })) as DesignMeta;

  return {graphicURL: data.previewUrl, isVideo: data.isVideo, name: data.designName} as GraphicMeta;
};

export const getFilteredDesigns = (designs: DesignSelection[], max: number): DesignSelection[] => {
  const sanitizedArray = designs.filter((design) => design.posterHashedId && design.pageHashedId);
  if (sanitizedArray.length > max) {
    sanitizedArray.splice(max);
  }

  return sanitizedArray;
};

export const getDesignSelectionsFromPageIdsMap = (pageIdsMap: Record<PosterHashedId, PageHashedId[]>): DesignSelection[] => {
  return Object.keys(pageIdsMap).flatMap((posterId): DesignSelection[] => {
    return pageIdsMap[posterId].map((pageId): DesignSelection => {
      return {posterHashedId: posterId, pageHashedId: pageId};
    });
  });
};

export const isSingleDesignSelected = (designs: DesignSelection[]): boolean => {
  return designs.length === 1;
};

export const getSelectedDesignCount = (designs: DesignSelection[]): number => {
  return designs.length;
};

export const getKeyForPublishParams = (accountId: SocialMediaAccountId): string => {
  return `publish-params-${accountId}`;
};

export const scrollToPublishParams = (scrollingContainer: HTMLElement, publishParamsElement: Element): void => {
  const elementTopWithinContainer = publishParamsElement.getBoundingClientRect().top - scrollingContainer.getBoundingClientRect().top + scrollingContainer.scrollTop;

  scrollingContainer.scrollTo({top: elementTopWithinContainer - PUBLISH_PARAMS_STICKY_HEADER_HEIGHT, behavior: 'smooth'});
};

export const scrollContainerToTop = (scrollingContainer: HTMLElement): void => {
  scrollingContainer.scrollTo({top: 0, behavior: 'smooth'});
};

export const jumpContainerToTop = (scrollingContainer: HTMLElement): void => {
  scrollingContainer.scrollTo({top: 0, behavior: 'instant'});
};

export const getDesignGalleryHeightForSelectDesignStep = (isMobileLayout: boolean): string => {
  return `${window.innerHeight - (isMobileLayout ? DESIGN_GALLERY_BOTTOM_OFFSET_MOBILE : DESIGN_GALLERY_BOTTOM_OFFSET)}px`;
};

export const getSanitizedEmphasizedPlatform = (emphasizedPlatform: SocialPlatform | undefined): SocialPlatform | undefined => {
  return emphasizedPlatform ?? undefined;
};

export const buildGraphicsMetaMap = (
  previewUrls: string[],
  designsIsVideo: boolean[],
  designNames: string[],
  designSelectionInformation: DesignSelection[] = []
): GraphicsMetaMapType => {
  const graphicsMetaMap: GraphicsMetaMapType = {};

  for (let i = 0; i < designSelectionInformation.length; i++) {
    const {posterHashedId, pageHashedId} = designSelectionInformation[i];

    graphicsMetaMap[posterHashedId] ??= {};
    graphicsMetaMap[posterHashedId][pageHashedId] = {
      isVideo: designsIsVideo[i],
      graphicURL: previewUrls[i],
      name: designNames[i],
      order: i,
    };
  }

  return graphicsMetaMap;
};

export const getWizardURLForEmphasizedPlatform = (platform: SocialPlatform, idTemplate?: string): string => {
  const getParamKey = 'emphasizedPlatform';

  if (idTemplate) {
    return window.PMW.util.site_url(`socialmedia/post/template/${idTemplate}?${getParamKey}=${platform}`);
  }

  return window.PMW.util.site_url(`socialmedia/post?${getParamKey}=${platform}`);
};
